import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useDebounce } from '../hooks/useDebounce';
import CameraModal from './CameraModal'; // Import the modal

// SVG Camera Icon
const CameraIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const SecurityPage = () => {
  const [vehicleNo, setVehicleNo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [odometer, setOdometer] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [status, setStatus] = useState({ loading: false, error: null, success: null });
  const [isCameraOpen, setIsCameraOpen] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    if (debouncedSearchTerm.length >= 3) {
      setIsSearching(true);
      axios.get(`/api/security/vehicles/search?term=${debouncedSearchTerm}`)
        .then(response => {
          const data = response.data;
          if (Array.isArray(data)) {
            setSuggestions(data);
          } else {
            // fallback if data is not an array
            setSuggestions([]);
            console.warn('Search API returned non-array data:', data);
          }
        })

        .catch(error => console.error('Search failed:', error))
        .finally(() => setIsSearching(false));
    } else {
      setSuggestions([]);
    }
  }, [debouncedSearchTerm]);

  const handleVehicleNoChange = (e) => {
    const value = e.target.value.toUpperCase();
    setVehicleNo(value);
    setSearchTerm(value);
  };

  const handleSuggestionClick = (suggestion) => {
    setVehicleNo(suggestion.vehicle_no);
    setSuggestions([]);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleCapture = (blob) => {
    const capturedFile = new File([blob], `odometer-${Date.now()}.jpg`, { type: 'image/jpeg' });
    setPhoto(capturedFile);
    setPhotoPreview(URL.createObjectURL(capturedFile));
    setIsCameraOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!photo) {
      setStatus({ loading: false, error: "Odometer photo is required.", success: null });
      return;
    }
    setStatus({ loading: true, error: null, success: null });

    const formData = new FormData();
    formData.append('vehicle_no', vehicleNo);
    formData.append('odometer_value', odometer);
    formData.append('odometer_photo', photo);

    try {
      const response = await axios.post('/api/security/odometer-reading', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setStatus({ loading: false, success: response.data.message, error: null });
      setVehicleNo('');
      setOdometer('');
      setPhoto(null);
      setPhotoPreview('');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Submission failed.';
      setStatus({ loading: false, error: errorMessage, success: null });
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gray-100 flex flex-col items-center p-4">
        <div className="w-full max-w-md">
          <header className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">SnapE Cabs</h1>
            <span className="text-xl font-semibold text-blue-600">Security Log</span>
          </header>

          <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md">
            <div className="mb-4 relative">
              <label htmlFor="vehicleNo" className="block text-sm font-medium text-gray-700 mb-1">Vehicle Number</label>
              <input
                type="text"
                id="vehicleNo"
                value={vehicleNo}
                onChange={handleVehicleNoChange}
                className="w-full p-2 border border-gray-300 rounded-md uppercase"
                placeholder="e.g., WB01MJ5555"
                autoComplete="off"
                required
              />
              {isSearching && <div className="absolute right-2 top-9 text-sm text-gray-500">Searching...</div>}
              {Array.isArray(suggestions) && suggestions.length > 0 && (
                <ul>
                  {suggestions.map((s, index) => (
                    <li key={index} onClick={() => handleSuggestionClick(s)} className="p-2 hover:bg-gray-100 cursor-pointer">
                      {s.vehicle_no}
                    </li>
                  ))}
                </ul>
              )}

            </div>

            <div className="mb-4">
              <label htmlFor="odometer" className="block text-sm font-medium text-gray-700 mb-1">Odometer Reading (km)</label>
              <input
                type="number"
                id="odometer"
                value={odometer}
                onChange={(e) => setOdometer(e.target.value.replace(/\D/g, ''))}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="e.g., 123456"
                required
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Odometer Photo</label>
              <div className="flex items-center space-x-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                <button type="button" onClick={() => setIsCameraOpen(true)} className="p-2 rounded-full hover:bg-gray-200">
                  <CameraIcon />
                </button>
              </div>
              {photoPreview && (
                <div className="mt-4">
                  <img src={photoPreview} alt="Odometer Preview" className="w-full max-w-xs mx-auto rounded-md" />
                </div>
              )}
            </div>

            <button type="submit" disabled={status.loading} className="w-full bg-blue-600 text-white p-3 rounded-md font-semibold hover:bg-blue-700 disabled:bg-blue-300">
              {status.loading ? 'Submitting...' : 'Submit Reading'}
            </button>

            {status.success && <div className="mt-4 text-center text-green-600">{status.success}</div>}
            {status.error && <div className="mt-4 text-center text-red-600">{status.error}</div>}
          </form>
        </div>
      </div>
      <CameraModal show={isCameraOpen} onCapture={handleCapture} onClose={() => setIsCameraOpen(false)} />
    </>
  );
};

export default SecurityPage;
